'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';
import WarningModal from '@/components/WarningModal';

interface ErrorModalContextType {
  showError: (message: string, title?: string) => void;
}

const ErrorModalContext = createContext<ErrorModalContextType | undefined>(undefined);

export const useErrorModal = () => {
  const context = useContext(ErrorModalContext);
  if (context === undefined) {
    throw new Error('useErrorModal must be used within an ErrorModalProvider');
  }
  return context;
};

interface ErrorModalProviderProps {
  children: ReactNode;
}

export const ErrorModalProvider: React.FC<ErrorModalProviderProps> = ({ children }) => {
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('Error');

  const showError = (message: string, title: string = 'Error') => {
    setErrorMessage(message);
    setErrorTitle(title);
    setErrorModalOpen(true);
  };

  const handleClose = () => {
    setErrorModalOpen(false);
    setErrorMessage('');
    setErrorTitle('Error');
  };

  return (
    <ErrorModalContext.Provider value={{ showError }}>
      {children}
      <WarningModal
        open={errorModalOpen}
        onClose={handleClose}
        type="error"
        title={errorTitle}
        message={errorMessage}
        primaryButtonText="OK"
        onPrimary={handleClose}
      />
    </ErrorModalContext.Provider>
  );
};
